import { create } from 'zustand';
import type { MaterialRequestDto, MaterialRequestSyncResultDto } from '../types';
import { materialRequestService } from '../services';
import { getApiErrorMessage } from '@/lib/getApiErrorMessage';

interface PagedParams {
  page: number;
  pageSize: number;
  sortBy?: string;
  sortDesc?: boolean;
}

interface MaterialRequestState {
  // List state
  requests: MaterialRequestDto[];
  isLoading: boolean;
  error: string | null;

  // Paged list state
  totalCount: number;
  totalPages: number;
  isLoadingPaged: boolean;
  pagedError: string | null;

  // Sync state
  isSyncing: boolean;
  lastSyncResult: MaterialRequestSyncResultDto | null;
  lastSyncTime: string | null;
  syncError: string | null;

  // Detail sheet state
  detailSheetOpen: boolean;
  selectedRequest: MaterialRequestDto | null;

  // Detail page state
  selectedRequestDetail: MaterialRequestDto | null;
  isLoadingDetail: boolean;
  detailError: string | null;

  // Actions
  fetchRequests: () => Promise<void>;
  fetchRequestsPaged: (params: PagedParams) => Promise<void>;
  fetchAvailableForPicklist: () => Promise<void>;
  refreshAvailableForPicklist: () => Promise<void>;
  syncFromNetSuite: (syncFromDate?: string) => Promise<MaterialRequestSyncResultDto | null>;
  fetchAndSync: () => Promise<void>;
  fetchRequestById: (id: number) => Promise<void>;
  clearSelectedRequestDetail: () => void;
  clearError: () => void;
  clearSyncError: () => void;
  openDetailSheet: (request: MaterialRequestDto) => void;
  closeDetailSheet: () => void;
}

export const useMaterialRequestStore = create<MaterialRequestState>()((set, get) => ({
  // List state
  requests: [],
  isLoading: false,
  error: null,

  // Paged list state
  totalCount: 0,
  totalPages: 0,
  isLoadingPaged: false,
  pagedError: null,

  // Sync state
  isSyncing: false,
  lastSyncResult: null,
  lastSyncTime: null,
  syncError: null,

  // Detail sheet state
  detailSheetOpen: false,
  selectedRequest: null,

  // Detail page state
  selectedRequestDetail: null,
  isLoadingDetail: false,
  detailError: null,

  fetchRequests: async () => {
    set({ isLoading: true, error: null });
    try {
      const requests = await materialRequestService.getAll();
      set({ requests, isLoading: false });
    } catch (error) {
      const message = getApiErrorMessage(error, 'Failed to fetch material requests');
      set({ error: message, isLoading: false });
    }
  },

  fetchRequestsPaged: async (params: PagedParams) => {
    set({ isLoadingPaged: true, pagedError: null });
    try {
      const result = await materialRequestService.getAllPaged(params);
      set({
        requests: result.items,
        totalCount: result.totalCount,
        totalPages: result.totalPages,
        isLoadingPaged: false,
      });
    } catch (error) {
      const message = getApiErrorMessage(error, 'Failed to fetch material requests');
      set({ pagedError: message, isLoadingPaged: false });
    }
  },

  fetchAvailableForPicklist: async () => {
    set({ isLoading: true, error: null });
    try {
      const requests = await materialRequestService.getAvailableForPicklist();
      set({ requests, isLoading: false });
    } catch (error) {
      const message = getApiErrorMessage(error, 'Failed to fetch available material requests');
      set({ error: message, isLoading: false });
    }
  },

  // Silent refresh - updates data without loading state (preserves UI state like pagination/scroll)
  refreshAvailableForPicklist: async () => {
    try {
      const requests = await materialRequestService.getAvailableForPicklist();
      set({ requests }); // Just swap data - no loading state change
    } catch (error) {
      // Silent fail - optionally could show a toast
      console.error('Failed to refresh:', error);
    }
  },

  syncFromNetSuite: async (syncFromDate?: string) => {
    set({ isSyncing: true, syncError: null });
    try {
      const result = await materialRequestService.syncFromNetSuite(syncFromDate);
      set({
        lastSyncResult: result,
        lastSyncTime: new Date().toISOString(),
        isSyncing: false,
      });
      return result;
    } catch (error) {
      const message = getApiErrorMessage(error, 'Failed to sync from NetSuite');
      set({ syncError: message, isSyncing: false });
      return null;
    }
  },

  fetchAndSync: async () => {
    // Start loading immediately to show cached data
    set({ isLoading: true, error: null });

    try {
      // Fetch existing data first (shows cached data quickly)
      const requests = await materialRequestService.getAll();
      set({ requests, isLoading: false });

      // Then sync in background
      const syncResult = await get().syncFromNetSuite();

      // If sync was successful and got new records, refresh the list
      if (syncResult?.success && syncResult.newRecordsCreated > 0) {
        const updatedRequests = await materialRequestService.getAll();
        set({ requests: updatedRequests });
      }
    } catch (error) {
      const message = getApiErrorMessage(error, 'Failed to fetch material requests');
      set({ error: message, isLoading: false });
    }
  },

  fetchRequestById: async (id: number) => {
    set({ isLoadingDetail: true, detailError: null });
    try {
      const request = await materialRequestService.getById(id);
      set({ selectedRequestDetail: request, isLoadingDetail: false });
    } catch (error) {
      const message = getApiErrorMessage(error, 'Failed to fetch material request');
      set({ detailError: message, isLoadingDetail: false });
    }
  },

  clearSelectedRequestDetail: () =>
    set({ selectedRequestDetail: null, detailError: null }),

  clearError: () => set({ error: null }),
  clearSyncError: () => set({ syncError: null }),

  openDetailSheet: (request: MaterialRequestDto) =>
    set({ detailSheetOpen: true, selectedRequest: request }),
  closeDetailSheet: () =>
    set({ detailSheetOpen: false, selectedRequest: null }),
}));
